/**
 * RulesDialog - Manage custom LLM rules for Invoice Entry (Purchase/Sales)
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, X, Plus } from 'lucide-react';
import { getInvoiceLabels } from '@/utils/invoiceLabels';

const API_URL = import.meta.env.VITE_API_URL || '';
const RULE_MAX_LENGTH = 150;

const TARGET_OPTIONS = [
  { value: 'ledger', label: 'Ledger' },
  { value: 'stock_item_service', label: 'Stock Item/Service' },
  { value: 'transaction_ledger', label: 'Transaction Ledger' },
  { value: 'expense_ledger', label: 'Expense Ledger' },
  { value: 'tax_ledger', label: 'Tax Ledger' },
];

interface InvoiceRule {
  id: string;
  company: string;
  ruleText: string;
  targetType: string;
}

interface RulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionType: 'purchase' | 'sales';
  company: string;
}

export const RulesDialog: React.FC<RulesDialogProps> = ({
  open,
  onOpenChange,
  transactionType,
  company: selectedCompany
}) => {
  const [rules, setRules] = useState<InvoiceRule[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [company, setCompany] = useState('');
  const [targetType, setTargetType] = useState('');
  const [ruleText, setRuleText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const token = localStorage.getItem('auth_token');

  // Get dynamic labels based on transaction type
  const labels = getInvoiceLabels(transactionType);

  const resetForm = () => {
    setEditingRuleId(null);
    setCompany('');
    setTargetType('');
    setRuleText('');
    setShowForm(false);
  };

  const fetchRules = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/invoice-entry/rules?company=${selectedCompany}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        console.error('[RulesDialog] Failed to fetch rules:', response.status, await response.text());
        return;
      }
      const data = await response.json();
      setRules(data.rules || []);
    } catch (error) {
      console.error('Failed to fetch invoice entry rules', error);
    }
  };

  const fetchCompanies = async () => {
    if (companies.length > 0) return;
    try {
      const response = await fetch(`${API_URL}/api/invoice-entry/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setCompanies(data || []);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchRules();
  }, [open]);

  const handleAddClick = () => {
    setEditingRuleId(null);
    setCompany('');
    setTargetType('');
    setRuleText('');
    setShowForm(true);
    fetchCompanies();
  };

  const handleEdit = (rule: InvoiceRule) => {
    setEditingRuleId(rule.id);
    setCompany(rule.company);
    setTargetType(rule.targetType);
    setRuleText(rule.ruleText);
    setShowForm(true);
    fetchCompanies();
  };

  const handleDelete = async (ruleId: string) => {
    await fetch(`${API_URL}/api/invoice-entry/rules/${ruleId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchRules();
  };

  const handleSave = async () => {
    const trimmed = ruleText.trim();
    if (!company || !targetType || !trimmed) return;

    setIsSaving(true);
    try {
      const payload = {
        company,
        transaction_type: transactionType,
        target_type: targetType,
        rule_text: trimmed,
      };

      if (editingRuleId) {
        await fetch(`${API_URL}/api/invoice-entry/rules/${editingRuleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        await fetch(`${API_URL}/api/invoice-entry/rules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      await fetchRules();
      resetForm();
    } finally {
      setIsSaving(false);
    }
  };

  const targetLabel = (value: string) => TARGET_OPTIONS.find((o) => o.value === value)?.label || value;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{labels.rulesDialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  if (!showForm) {
                    handleAddClick();
                  } else {
                    setShowForm(false);
                  }
                }}
              >
                <Plus className="h-4 w-4 mr-2" /> Add Rule
              </Button>
            </div>

            {showForm && (
              <div className="rounded-lg border p-3 space-y-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Company</label>
                <Select value={company} onValueChange={setCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">What will the rule help identify?</label>
                <Select value={targetType} onValueChange={setTargetType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target" />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Rule</label>
                <Textarea
                  value={ruleText}
                  onChange={(event) => setRuleText(event.target.value)}
                  maxLength={RULE_MAX_LENGTH}
                  placeholder="Enter rule (max 150 characters)"
                />
                <div className="text-xs text-muted-foreground text-right">
                  {ruleText.length}/{RULE_MAX_LENGTH}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} disabled={!company || !targetType || !ruleText.trim() || isSaving}>
                  {editingRuleId ? 'Update Rule' : 'Save Rule'}
                </Button>
                <Button variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
              </div>
            )}
          </div>

          <div className="border rounded-lg overflow-hidden w-full">
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left whitespace-nowrap w-1/5">Company</th>
                    <th className="p-2 text-left whitespace-nowrap w-1/5">Target</th>
                    <th className="p-2 text-left w-2/5">Rule</th>
                    <th className="p-2 text-right whitespace-nowrap w-1/5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 ? (
                    <tr>
                      <td className="p-3 text-sm text-muted-foreground" colSpan={4}>
                        No rules created yet.
                      </td>
                    </tr>
                  ) : (
                    rules.map((rule) => (
                      <tr key={rule.id} className="border-t">
                        <td className="p-2 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{rule.company}</td>
                        <td className="p-2 text-sm text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">{targetLabel(rule.targetType)}</td>
                        <td className="p-2 text-sm text-muted-foreground overflow-hidden text-ellipsis" title={rule.ruleText}>
                          {rule.ruleText}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(rule)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(rule.id)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
              </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RulesDialog;
